import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, Alert } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'

const { Title, Text } = Typography

export default function LoginPage() {
  const navigate = useNavigate()
  const setUtente = useAuthStore((s) => s.setUtente)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true)
    setError(null)
    try {
      const data = await authApi.login(values.email, values.password)
      const { utente, access_token } = data
      setUtente(utente)

      // Avvia GPS background se: terzista loggato dall'app mobile + token disponibile
      const w = window as unknown as {
        __EDR_IS_MOBILE_APP__?: boolean
        EDRNative?: { call: (fn: string, args?: unknown) => void }
      }
      if (utente.ruoli.includes('terzista') && w.__EDR_IS_MOBILE_APP__ && w.EDRNative && access_token) {
        try {
          // Recupero terzista_id dal backend
          const meRes = await fetch('/api/v1/mobile/me', {
            headers: { Authorization: `Bearer ${access_token}`, 'X-EDR-Client': 'mobile' },
          })
          if (!meRes.ok) {
            localStorage.setItem('edr.gps.debug', `mobile/me failed: ${meRes.status}`)
          } else {
            const me = await meRes.json()
            if (!me.terzista_id) {
              localStorage.setItem('edr.gps.debug', 'terzista_id assente in /mobile/me')
            } else {
              // Listener per la risposta del bridge — la salvo in localStorage
              // così MobileLayout può mostrarla anche dopo il navigate.
              const handler = (ev: Event) => {
                const me2 = ev as MessageEvent
                try {
                  const data = typeof me2.data === 'string' ? JSON.parse(me2.data) : me2.data
                  if (data?.fn === 'startGpsTracking') {
                    const txt = data.ok ? 'GPS attivo' : `GPS ko: ${data.error || 'errore sconosciuto'}`
                    localStorage.setItem('edr.gps.debug', txt)
                    localStorage.setItem('edr.gps.status', data.ok ? 'on' : 'off')
                    window.removeEventListener('edr:nativeResponse', handler as EventListener)
                  }
                } catch { /* ignore */ }
              }
              window.addEventListener('edr:nativeResponse', handler as EventListener)
              localStorage.setItem('edr.gps.debug', 'startGpsTracking inviato — in attesa risposta…')
              localStorage.setItem('edr.gps.status', 'pending')

              const apiUrl = `${window.location.origin}/api/v1`
              w.EDRNative.call('startGpsTracking', {
                terzistaId: me.terzista_id,
                apiUrl,
                accessToken: access_token,
                interventoId: null,
              })
            }
          }
        } catch (e: unknown) {
          localStorage.setItem('edr.gps.debug', `eccezione: ${String((e as Error).message ?? e)}`)
        }
      }

      // Terzisti: sempre UI mobile. Ruoli interni: sempre gestionale completo,
      // anche da smartphone (decisione di Luca 13/07/2026 — i terzisti usano
      // l'app nativa, la UI mobile web resta raggiungibile a mano su /m).
      const useMobileUi = utente.ruoli.includes('terzista')
      navigate(useMobileUi ? '/m/interventi' : '/', { replace: true })
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      setError(msg || 'Errore di connessione. Riprova.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a1628 0%, #1a3a6b 100%)',
    }}>
      <Card
        style={{
          width: 400,
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
        styles={{ body: { padding: '40px 40px 32px' } }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/logo.png"
            alt="Edr Group"
            style={{ maxHeight: 72, maxWidth: 240, objectFit: 'contain' }}
          />
          <Title level={4} style={{ marginTop: 16, marginBottom: 4, color: '#1a3a6b' }}>
            APP EDR 4.0
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Gestione Facility Management
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
        )}

        <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Inserisci la tua email' },
              { type: 'email', message: 'Email non valida' },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#aaa' }} />}
              placeholder="Email"
              size="large"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Inserisci la password' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#aaa' }} />}
              placeholder="Password"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              block
              style={{
                background: 'linear-gradient(90deg, #1a3a6b, #2563eb)',
                border: 'none',
                borderRadius: 8,
                height: 46,
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              Accedi
            </Button>
          </Form.Item>
        </Form>

        <Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center', marginTop: 24, fontSize: 12 }}
        >
          © {new Date().getFullYear()} Edr Group — Tutti i diritti riservati
        </Text>
      </Card>
    </div>
  )
}
